import { fetchWithAuth, getDefaultHeaders, SERVER_URL } from '../config/api';
import { errorMessage, requestJson } from '../config/apiError';
import type { PlanPriceTier } from '../utils/seatPricing';

// Helper to construct full URLs correctly
const createEndpoint = (path: string): string => {
    return `/api/${path}`;
};

// Helper function to get server URL with matching protocol
const getServerUrlWithMatchingProtocol = () => SERVER_URL;

// Subscription API endpoints
export const SUBSCRIPTION_ENDPOINTS = {
    PLANS: createEndpoint('stripe/plans'),
    SUBSCRIPTIONS: createEndpoint('stripe/subscriptions'),
    PAYMENT_INTENTS: createEndpoint('stripe/payment-intents'),
    SUBSCRIPTION: createEndpoint('stripe/subscription'),
    CANCEL_SUBSCRIPTION: createEndpoint('stripe/cancel-subscription'),
    UPDATE_PAYMENT_METHOD: createEndpoint('stripe/update-payment-method'),
    VALIDATE_COUPON: createEndpoint('stripe/validate-coupon'),
    PAY_INVOICE: createEndpoint('stripe/pay-invoice'),
    BILLING_PORTAL: createEndpoint('stripe/billing-portal'),
    CHECKOUT_SESSION: createEndpoint('stripe/checkout-session'),
    SANDBOX_CHECKOUT_SESSION: createEndpoint('sandbox/stripe/checkout-session'),
    BUSINESS: createEndpoint('Business'),
    BUSINESS_ME: createEndpoint('Business/me'),
    SANDBOX_PLANS: createEndpoint('sandbox/stripe/plans'),
    SANDBOX_SUBSCRIPTIONS: createEndpoint('sandbox/stripe/subscriptions'),
    UPDATE_COUNCIL_AREAS: createEndpoint('stripe/subscriptions/council-areas'),
    SANDBOX_UPDATE_COUNCIL_AREAS: createEndpoint('sandbox/stripe/subscriptions/council-areas'),
    CHANGE_PLAN: createEndpoint('stripe/subscriptions/change-plan'),
    CHANGE_SEATS: createEndpoint('stripe/subscriptions/seats'),
    RETRY_PAYMENT: createEndpoint('stripe/retry-payment'),
    SANDBOX_RETRY_PAYMENT: createEndpoint('sandbox/stripe/retry-payment'),
};

// Subscription API types
export type Plan = {
    id: string;
    name: string;
    description: string;
    amount: number;
    currency: string;
    interval: string;
    priceId: string;
    /** Stable config key — 'marketplace' | 'intelligence' | 'marketplaceFeature' (StripePriceKeys). */
    key?: string;
    /** The price's own `unit_amount`. NULL on a tiered price — read it directly and you quote $0. */
    unitAmountMinor?: number | null;
    /** Stripe `tiers_mode` ('volume'/'graduated'); null on a per-unit price. */
    tiersMode?: string | null;
    /** Expanded `price.tiers`; null/empty on a per-unit price. Feed these to seatTotalMinor(). */
    tiers?: PlanPriceTier[] | null;
};

/**
 * What a plan change turned out to be, priced SERVER-SIDE.
 *
 * The caller never says which direction it goes — it is worked out from the price, so a client
 * cannot get somebody charged for spending less. `effectiveAt` is the answer that matters on a
 * downgrade: the change waits for the end of the period already paid for.
 */
export type PlanChangeResult = {
    /**
     * `cancelled` means the requested plan was the one they were already on AND a scheduled change
     * to something else was dropped — i.e. a downgrade was undone. Distinct from `nochange`, where
     * there was genuinely nothing pending.
     */
    kind: 'upgrade' | 'downgrade' | 'nochange' | 'cancelled';
    /** When the new plan starts. Now for an upgrade; the period end for a downgrade. */
    effectiveAt: string | null;
    /** Charged immediately, in CENTS. Always 0 for a downgrade. */
    amountChargedMinor: number;
    invoiceId: string | null;
};

export type BusinessInfo = {
    name: string;
    email: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    description?: string;
    website?: string;
    councilAreas?: string[];
    keywords?: string[];
    categories?: string[];
    industries?: string[];
    subscriptionPlan?: string;
    termsAcceptedAt?: string;
    // Business settings
    mainContactName?: string;
    contactEmail?: string;
};

export type CouncilAvailability = {
    name: string;
    spotsLeft: number;
};

export type BusinessResponse = {
    id: string;
    name: string;
    email: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    description?: string;
    website?: string;
    councilAreas?: string[];
    keywords?: string[];
    categories?: string[];
    subscriptionPlan?: string;
    stripeCustomerId?: string;
    ownerId?: string;
    createdAt?: string;
    updatedAt?: string;
    termsAcceptedAt?: string;
    // Business settings
    mainContactName?: string;
    contactEmail?: string;
};

export type PaymentIntentResult = {
    clientSecret: string;
    amount: number;
    currency: string;
    status: string;
};

export type CouponValidationResult = {
    isValid: boolean;
    code: string;
    discountType?: string;
    discountAmount?: number;
    message?: string;
};

export type UserInfo = {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
};

/**
 * POSTs a coupon code to a validate-coupon endpoint (live or sandbox). Any failure resolves to
 * `{ isValid: false, message }` with a message fit to show: the server's own for a 4xx, friendly
 * copy for a 5xx / HTML page / network failure — never the raw response body.
 */
export async function validateCouponAt(url: string, couponCode: string, fallback: string): Promise<CouponValidationResult> {
    try {
        return await requestJson<CouponValidationResult>(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ CouponCode: couponCode }),
        });
    } catch (error) {
        return { isValid: false, code: couponCode, message: errorMessage(error, fallback) };
    }
}

// Subscription API functions
// Create a single export for all subscription API functions
export const subscriptionApi = {
    // Get all subscription plans
    getPlans: async (): Promise<Plan[]> => {
        return fetchWithAuth<Plan[]>(SUBSCRIPTION_ENDPOINTS.PLANS, {
            method: 'GET',
            headers: getDefaultHeaders()
        });
    },
    
    // Validate a coupon code
    validateCoupon: async (couponCode: string): Promise<CouponValidationResult> =>
        // No authentication needed for coupon validation.
        validateCouponAt(
            `${getServerUrlWithMatchingProtocol()}/${SUBSCRIPTION_ENDPOINTS.VALIDATE_COUPON.replace(/^\/*/g, '')}`,
            couponCode,
            'Failed to validate coupon',
        ),
    // Removed duplicate getPlans function

    // Create a new subscription
    // No `quantity`: one subscription is one plan, not one per council. The server pins the Stripe
    // quantity to 1 regardless, so sending a council count here would achieve nothing but confusion.
    createSubscription: async (priceId: string, paymentMethodId: string, businessId: string, userInfo?: UserInfo, couponCode?: string, occurrence?: string) => {
        if (!businessId) {
            throw new Error('BusinessId is required');
        }

        // Create a properly structured object with optional user info and coupon code
        const requestData = {
            BusinessId: businessId,
            PriceId: priceId,
            PaymentMethodId: paymentMethodId,
            CustomerId: null, // Let the backend create a new customer
            ...(userInfo && {
                UserInfo: {
                    Email: userInfo.email,
                    FirstName: userInfo.firstName,
                    LastName: userInfo.lastName,
                    Password: userInfo.password
                }
            }),
            ...(couponCode && { CouponCode: couponCode }),
            ...(occurrence && { Occurrence: occurrence })
        };

        // Use fetchWithAuth like other functions to ensure proper URL construction
        return fetchWithAuth(SUBSCRIPTION_ENDPOINTS.SUBSCRIPTIONS, {
            method: 'POST',
            headers: getDefaultHeaders(),
            body: JSON.stringify(requestData)
        });
    },

    // Create a payment intent for a one-time payment
    createPaymentIntent: async (amount: number, description: string, currency: string = 'aud') => {
        return fetchWithAuth<PaymentIntentResult>(SUBSCRIPTION_ENDPOINTS.PAYMENT_INTENTS, {
            method: 'POST',
            headers: getDefaultHeaders(),
            body: JSON.stringify({ amount, description, currency })
        });
    },

    // Check if an email is already registered
    checkEmailExists: async (email: string): Promise<{ processed: boolean }> => {
        return fetchWithAuth<{ processed: boolean }>(
            createEndpoint(`user/check-email?email=${encodeURIComponent(email)}`),
            { method: 'GET', headers: getDefaultHeaders() }
        );
    },

    // Get available council areas (optionally scoped to a state, e.g. "SA" or "QLD")
    getCouncilAvailability: async (state?: string): Promise<{ councils: CouncilAvailability[] }> => {
        const path = state ? `Business/council-availability?state=${encodeURIComponent(state)}` : 'Business/council-availability';
        const fullUrl = `${getServerUrlWithMatchingProtocol()}/${createEndpoint(path).replace(/^\/*/g, '')}`;
        const response = await fetch(fullUrl, {
            method: 'GET',
            headers: { 'Accept': 'application/json' }
        });
        if (!response.ok) {
            throw new Error('Failed to fetch council availability');
        }
        return response.json();
    },

    // Update council areas. Not a billing call: councils are included in the plan, so this touches
    // no Stripe subscription. `pendingRemovals` only ever carries records left over from the old
    // per-council model, still draining server-side.
    updateCouncilAreas: async (councilAreas: string[]): Promise<{ councilAreas: string[]; pendingRemovals?: { councilName: string; effectiveAt: string }[] }> => {
        return fetchWithAuth<{ councilAreas: string[]; pendingRemovals?: { councilName: string; effectiveAt: string }[] }>(SUBSCRIPTION_ENDPOINTS.UPDATE_COUNCIL_AREAS, {
            method: 'PUT',
            headers: getDefaultHeaders(),
            body: JSON.stringify({ councilAreas })
        });
    },

    // Update council areas for sandbox business
    updateSandboxCouncilAreas: async (businessId: string, councilAreas: string[]): Promise<{ councilAreas: string[]; pendingRemovals?: { councilName: string; effectiveAt: string }[] }> => {
        return fetchWithAuth<{ councilAreas: string[]; pendingRemovals?: { councilName: string; effectiveAt: string }[] }>(SUBSCRIPTION_ENDPOINTS.SANDBOX_UPDATE_COUNCIL_AREAS, {
            method: 'PUT',
            headers: getDefaultHeaders(),
            body: JSON.stringify({ businessId, councilAreas })
        });
    },

    // NOTE: `previewCouncilProration` is gone, along with the server endpoints behind it. Councils
    // are included in the plan, so adding one charges nothing and there is no proration to preview.

    // Get current user's subscription details
    getSubscription: async () => {
        return fetchWithAuth(SUBSCRIPTION_ENDPOINTS.SUBSCRIPTION, {
            method: 'GET',
            headers: getDefaultHeaders()
        });
    },

    // Cancel subscription
    cancelSubscription: async () => {
        return fetchWithAuth(SUBSCRIPTION_ENDPOINTS.CANCEL_SUBSCRIPTION, {
            method: 'POST',
            headers: getDefaultHeaders(),
            body: JSON.stringify({})
        });
    },

    // Update payment method (main implementation)
    updatePaymentMethod: async (paymentMethodId: string) => {
        return fetchWithAuth(SUBSCRIPTION_ENDPOINTS.UPDATE_PAYMENT_METHOD, {
            method: 'POST',
            headers: getDefaultHeaders(),
            body: JSON.stringify({ paymentMethodId })
        });
    },

    // Get payment intents for subscription
    getPaymentIntents: async () => {
        return fetchWithAuth(SUBSCRIPTION_ENDPOINTS.PAYMENT_INTENTS, {
            method: 'GET',
            headers: getDefaultHeaders()
        });
    },

    // Create a business
    createBusiness: async (businessData: BusinessInfo): Promise<BusinessResponse> => {
        // We'll let the server handle the OwnerId field
        // The server will set it to string.Empty, but we need to make sure
        // it's properly handled during MongoDB serialization
        
        return fetchWithAuth<BusinessResponse>(SUBSCRIPTION_ENDPOINTS.BUSINESS, {
            method: 'POST',
            headers: getDefaultHeaders(),
            body: JSON.stringify({
                ...businessData,
                // Don't include ownerId - let the server handle it
                // Ensure all optional fields are included with empty strings if not provided
                description: businessData.description || '',
                website: businessData.website || '',
                phone: businessData.phone || '',
                address: businessData.address || '',
                city: businessData.city || '',
                state: businessData.state || '',
                postalCode: businessData.postalCode || '',
                country: businessData.country || 'Australia',
                councilAreas: businessData.councilAreas || [],
                keywords: businessData.keywords || [],
                categories: businessData.categories || [],
                subscriptionPlan: businessData.subscriptionPlan || '',
                termsAcceptedAt: businessData.termsAcceptedAt || null
            }),
            credentials: 'include' // Include credentials if needed for CORS
        });
    },

    // Get current user's business
    getBusiness: async (): Promise<BusinessResponse> => {
        return fetchWithAuth<BusinessResponse>(SUBSCRIPTION_ENDPOINTS.BUSINESS_ME, {
            method: 'GET',
            headers: getDefaultHeaders()
        });
    },

    // This is a duplicate function that was removed
    // The main updatePaymentMethod function is defined above
    
    // Create a billing portal session
    createBillingPortalSession: async (): Promise<{ url: string }> => {
        return fetchWithAuth<{ url: string }>(SUBSCRIPTION_ENDPOINTS.BILLING_PORTAL, {
            method: 'POST',
            headers: getDefaultHeaders(),
        });
    },

    // Start a subscription via Stripe Checkout (redirect to the returned url)
    // `seats` is the Stripe QUANTITY: the number of PEOPLE on the account. It is never a council
    // count — councils are free and unlimited, and billing per council is the bug removed in
    // 0d062917. Omit it for Marketplace ($0, always one unit); the server clamps regardless.
    //
    // `councilAreas` is a DELIVERY PREFERENCE, not a price input, and it is genuinely optional:
    // the server only writes it when it is non-null, so passing `[]` would WIPE the business's
    // council delivery. Callers with nothing to say (registration) must pass null, not an empty
    // array.
    createCheckoutSession: async (priceId: string, councilAreas: string[] | null, isSandbox = false, couponCode?: string, seats?: number): Promise<{ url: string }> => {
        const endpoint = isSandbox ? SUBSCRIPTION_ENDPOINTS.SANDBOX_CHECKOUT_SESSION : SUBSCRIPTION_ENDPOINTS.CHECKOUT_SESSION;
        return fetchWithAuth<{ url: string }>(endpoint, {
            method: 'POST',
            headers: getDefaultHeaders(),
            body: JSON.stringify({
                priceId,
                ...(councilAreas ? { councilAreas } : {}),
                ...(couponCode ? { couponCode } : {}),
                ...(seats ? { seats } : {}),
            }),
        });
    },

    // Move an EXISTING subscription onto another plan or seat count, without Checkout.
    //
    // `seats` is PEOPLE, as everywhere else — never a council count (0d062917). The direction is
    // NOT sent: the server prices both plans and decides, then charges an upgrade at once and
    // schedules a downgrade for the end of the paid period.
    //
    // Throws with `response.data.code === 'no_subscription'` when the business has nothing to
    // change — the caller should open Checkout instead, which is where a card can be collected.
    changePlan: async (priceId: string, seats?: number): Promise<PlanChangeResult> => {
        return fetchWithAuth<PlanChangeResult>(SUBSCRIPTION_ENDPOINTS.CHANGE_PLAN, {
            method: 'POST',
            headers: getDefaultHeaders(),
            body: JSON.stringify({ priceId, ...(seats ? { seats } : {}) }),
        });
    },

    // Change ONLY how many people the subscription covers, leaving the plan alone.
    //
    // `seats` is the TOTAL head count, not how many to add: an absolute count is idempotent, so a
    // double-submitted request buys nothing the second time where "add one" would buy two seats.
    // Adding is dearer, so it is charged now and prorated; removing waits for the period end.
    changeSeats: async (seats: number): Promise<PlanChangeResult & { seats: number }> => {
        return fetchWithAuth<PlanChangeResult & { seats: number }>(SUBSCRIPTION_ENDPOINTS.CHANGE_SEATS, {
            method: 'POST',
            headers: getDefaultHeaders(),
            body: JSON.stringify({ seats }),
        });
    },

    // Pay an unpaid invoice
    payInvoice: async (invoiceId: string): Promise<{success: boolean}> => {
        return fetchWithAuth<{success: boolean}>(SUBSCRIPTION_ENDPOINTS.PAY_INVOICE, {
            method: 'POST',
            headers: getDefaultHeaders(),
            body: JSON.stringify({ invoiceId })
        });
    },

    // Get sandbox subscription plans
    getSandboxPlans: async (): Promise<Plan[]> => {
        return fetchWithAuth<Plan[]>(SUBSCRIPTION_ENDPOINTS.SANDBOX_PLANS, {
            method: 'GET',
            headers: getDefaultHeaders()
        });
    },

    // Create a sandbox subscription (resubscribe or new)
    createSandboxSubscription: async (priceId: string, paymentMethodId: string, businessId: string) => {
        return fetchWithAuth(SUBSCRIPTION_ENDPOINTS.SANDBOX_SUBSCRIPTIONS, {
            method: 'POST',
            headers: getDefaultHeaders(),
            body: JSON.stringify({
                BusinessId: businessId,
                PriceId: priceId,
                PaymentMethodId: paymentMethodId
            })
        });
    },

    // Retry payment for an incomplete subscription
    retryPayment: async (): Promise<{ success: boolean; subscriptionStatus: string }> => {
        return fetchWithAuth<{ success: boolean; subscriptionStatus: string }>(SUBSCRIPTION_ENDPOINTS.RETRY_PAYMENT, {
            method: 'POST',
            headers: getDefaultHeaders(),
        });
    },

    // Retry payment for an incomplete sandbox subscription
    retrySandboxPayment: async (businessId: string): Promise<{ success: boolean; subscriptionStatus: string }> => {
        return fetchWithAuth<{ success: boolean; subscriptionStatus: string }>(SUBSCRIPTION_ENDPOINTS.SANDBOX_RETRY_PAYMENT, {
            method: 'POST',
            headers: getDefaultHeaders(),
            body: JSON.stringify({ BusinessId: businessId }),
        });
    },
};
